import {Alert, Card, Space, Spin, Steps, Row, Col} from "antd";
import {useEffect, useRef, useState, RefObject} from "react";
import {DownOutlined, UpOutlined} from "@ant-design/icons";
import CheckDeclined from "./CheckDeclined";
import MagicButton from "./sidebars/AIAssistant/utils/MagicButton";
import { MagicIcon } from "./sidebars/AIAssistant/utils/MagicButton";
import MinimumInfo from "./MinimumInfo";
import FastQuotation from "./FastQuotation";
import {useGetNegotiationDetailQuery} from "../../redux/api/negotiationsApiSlice";
import {useParams} from "react-router-dom";
import {useGetAIThoughts} from "./utils/useGetAIThoughts";
import {skipToken} from "@reduxjs/toolkit/query";
import {assistantBg, assistantBorder, assistantColor, CustomRobotIcon} from "./sidebars/AIAssistant/utils/CustomRobotIcon";
import {usePolling} from "../../hooks/PollingContext";
import {JOB_STATUS} from "../../types/reconciliation";
import ReactMarkdown from "react-markdown";
import {Typography} from 'antd';
import RaterButton from '../RaterButton'
import { euro } from "../../utils/formatters";
import FastQuotationMessages from "./utils/FastQuotationMessages";

const {Title} = Typography

export default function AnalysisCard(
    {
        callBackUpdateNegotiation,
        forwaredRef
    }: {
        callBackUpdateNegotiation: () => void,
        forwaredRef: RefObject<HTMLDivElement>
    }
) {
    const {id} = useParams()

    const {data: negotiationData} = useGetNegotiationDetailQuery(id ?? skipToken)

    const [expanded, setExpanded] = useState<number[]>([]);
    const [currentStep, setCurrentStep] = useState<number>(0)
    const {isPolling, startPolling, stopPolling} = usePolling();

    const isPollingStarted = useRef(false);

    const checkThoughtAvailable = useGetAIThoughts()

    useEffect(() => {
        if (negotiationData && negotiationData.negotiation_analysis) {
            const step = negotiationData.negotiation_analysis.analysis_step
            setCurrentStep(step)
            setExpanded(
                Array.from(
                    { length: step + 1 },
                    (_, i) => i
                )
            )
        }
    }, [negotiationData]);

    const toggle = (idx: number) => {
        if (idx <= currentStep)
            setExpanded(prev =>
                prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
            );
    };

    useEffect(() => {
        if (negotiationData && negotiationData.negotiation_analysis) {
            if (id && negotiationData.negotiation_analysis.analysis_status === "P" && !isPollingStarted.current) {
                isPollingStarted.current = true;
                startPolling(id, callBackUpdateNegotiation);
            }
        }
    }, [callBackUpdateNegotiation, id, negotiationData, startPolling]);

    useEffect(() => {
        return () => {
            isPollingStarted.current = false;
            stopPolling(() => {
            });
        };
    }, [stopPolling]);

    return (
        <Card
            ref={forwaredRef}
            id="AIAnalysis"
            bordered={false}
            style={{
                borderRadius: '12px',
                width: '100%',
            }}
        >
            <Row style={{ marginBottom: '24px' }}>
                <Col>
                    <Title level={3}>
                        <MagicIcon style={{ fontSize: '24px' }} />
                    </Title>
                </Col>
                <Col style={{marginLeft: '12px'}}>
                    <Title level={3}>
                        Analisi della trattativa
                    </Title>
                </Col>
            </Row>

            {isPolling && (
                <Alert
                    style={{
                        color: assistantColor,
                        backgroundColor: assistantBg,
                        borderColor: assistantBorder,
                        marginBottom: '24px',
                    }}
                    type={"info"}
                    message={<strong style={{color: assistantColor}}>ERIK:</strong>}
                    description={<div><Spin/> Sto elaborando i dati, ci vorrà un pò...</div>}
                    showIcon
                    icon={<CustomRobotIcon/>}
                />
            )}

            {negotiationData?.negotiation_analysis?.analysis_status === JOB_STATUS.ERROR &&
                <Alert
                    message="Errore"
                    description={"Si è verificato un errore nell'analisi della trattativa."}
                    type="error"
                    showIcon
                    style={{margin: "16px"}}
                />
            }

            {!isPolling &&
                <>
                    <Steps style={{paddingRight: '24px'}} direction="vertical" size="small" current={currentStep}>
                        <Steps.Step
                            key="check_decline"
                            title={
                                <Space
                                    onClick={() => toggle(0)}
                                    style={{cursor: 0 > currentStep ? 'not-allowed' : 'pointer', userSelect: 'none'}}
                                >
                                    <strong>Check Decline</strong>
                                    {
                                        expanded.includes(0)
                                        ? <UpOutlined/>
                                        : <DownOutlined/>
                                    }
                                    {
                                        checkThoughtAvailable("check_decline")
                                        && (
                                            <div onClick={e => e.stopPropagation()}>
                                                <MagicButton
                                                    fieldValue="check_decline"
                                                    fieldLabel="Check Decline"
                                                />
                                            </div>
                                        )
                                    }
                                </Space>
                            }
                            description={
                                expanded.includes(0)
                                ? <CheckDeclined
                                    callBackUpdateNegotiation={() => callBackUpdateNegotiation()}
                                    isCurrentStep={currentStep === 0}
                                    startPolling={() => id && startPolling(id, callBackUpdateNegotiation)}
                                />
                                : null
                            }
                        />
                        <Steps.Step
                            key="minimum_info"
                            title={
                                <Space
                                    onClick={() => toggle(1)}
                                    style={{cursor: 1 > currentStep ? 'not-allowed' : 'pointer', userSelect: 'none'}}
                                >
                                    <strong>Check Completezza documentale</strong>
                                    {
                                        expanded.includes(1)
                                        ? <UpOutlined/>
                                        : <DownOutlined/>
                                    }
                                    {
                                        checkThoughtAvailable("minimum_info_check")
                                        && (
                                            <div onClick={e => e.stopPropagation()}>
                                                <MagicButton
                                                    fieldValue="minimum_info_check"
                                                    fieldLabel="Minimum Info"
                                                />
                                            </div>
                                        )
                                    }
                                </Space>
                            }
                            description={
                                expanded.includes(1)
                                ? <MinimumInfo
                                    isCurrentStep={currentStep === 1}
                                    callBackUpdateNegotiation={() => callBackUpdateNegotiation()}
                                    startPolling={() => id && startPolling(id, callBackUpdateNegotiation)}
                                />
                                : null
                            }
                        />
                        <Steps.Step
                            key="fast_quotation_eligibility"
                            title={
                                <Space
                                    onClick={() => toggle(2)}
                                    style={{cursor: 2 > currentStep ? 'not-allowed' : 'pointer', userSelect: 'none'}}
                                >
                                    <strong>Fast Quotation</strong>
                                    {
                                        expanded.includes(2)
                                        ? <UpOutlined/>
                                        : <DownOutlined/>
                                    }
                                    {
                                        checkThoughtAvailable("fast_quotation_eligibility")
                                        && (
                                            <div onClick={e => e.stopPropagation()}>
                                                <MagicButton
                                                    fieldValue="fast_quotation_eligibility"
                                                    fieldLabel="Fast Quotation Eligibility"
                                                />
                                            </div>
                                        )
                                    }
                                </Space>
                            }
                            description={
                                expanded.includes(2)
                                ? <FastQuotation
                                    isCurrentStep={currentStep === 2}
                                    startPolling={() => id && startPolling(id, callBackUpdateNegotiation)}
                                    callBackUpdateNegotiation={() => callBackUpdateNegotiation()}
                                />
                                : null
                            }
                        />
                    </Steps>
                </>
            }
            <Row style={{ marginBottom: '24px' }}>
                <Col>
                    <Title level={4}>
                        Calcolo del premio Fast Quotation
                    </Title>
                </Col>
            </Row>
            {negotiationData?.negotiation_analysis && <FastQuotationMessages negotiationAnalysis={negotiationData.negotiation_analysis}/>}
            <Row justify={"center"}>
                <RaterButton
                    callbackSaveData={()=> callBackUpdateNegotiation()}
                />
            </Row>
        </Card>
    );
}
